import { createCoachingApplicationFn } from "@/actions/coaching-application/handler";
import { readCampaignAttribution } from "@/components/analytics/attribution";
import { readMetaBrowserIds } from "@/components/analytics/conversions";
import {
  trackCoachingFormError,
  trackCoachingFormStart,
  trackCoachingFormSubmit,
  trackCoachingLead,
} from "@/components/analytics/conversions";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useMutation } from "@tanstack/react-query";
import { CheckCircle2 } from "lucide-react";
import { type FormEvent, type ReactNode, useState } from "react";

export function CoachingApplicationDialog({
  packageTitle,
  children,
}: {
  packageTitle: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [completed, setCompleted] = useState(false);

  const mutation = useMutation({
    mutationFn: createCoachingApplicationFn,
    onSuccess: (response) => {
      if (response.status !== 201) {
        trackCoachingFormError(packageTitle);
        return;
      }
      if (response.body.applicationId)
        trackCoachingLead(response.body.applicationId);
      setCompleted(true);
    },
    onError: () => trackCoachingFormError(packageTitle),
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    trackCoachingFormSubmit(packageTitle);
    const form = new FormData(event.currentTarget);
    const attribution = readCampaignAttribution();
    const meta = readMetaBrowserIds();
    mutation.mutate({
      data: {
        name: String(form.get("name") || ""),
        email: String(form.get("email") || ""),
        phone: String(form.get("phone") || ""),
        goal: String(form.get("goal") || ""),
        experience: String(form.get("experience") || ""),
        message: String(form.get("message") || "") || undefined,
        website: String(form.get("website") || "") || undefined,
        packageTitle,
        source: attribution?.source,
        medium: attribution?.medium,
        campaign: attribution?.campaign,
        gclid: attribution?.gclid,
        fbclid: attribution?.fbclid,
        metaFbp: meta.fbp,
        metaFbc: meta.fbc,
      },
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next && !open) trackCoachingFormStart(packageTitle);
        setOpen(next);
        if (!next) setCompleted(false);
      }}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        {completed ? (
          <div className="space-y-4 py-8 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-primary" />
            <DialogTitle>Başvurunuz alındı</DialogTitle>
            <p className="text-muted-foreground text-sm">
              Sizinle en kısa sürede iletişime geçeceğiz.
            </p>
            <Button onClick={() => setOpen(false)}>Tamam</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Koçluk Başvurusu</DialogTitle>
              <DialogDescription>
                Hedeflerinizi paylaşın; size uygun çalışma planını birlikte
                belirleyelim.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                name="website"
                tabIndex={-1}
                autoComplete="off"
                className="hidden"
                aria-hidden="true"
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="application-name">Ad Soyad</Label>
                  <Input
                    id="application-name"
                    name="name"
                    required
                    minLength={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="application-phone">Telefon</Label>
                  <Input
                    id="application-phone"
                    name="phone"
                    type="tel"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="application-email">E-posta</Label>
                <Input
                  id="application-email"
                  name="email"
                  type="email"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="application-experience">
                  Antrenman deneyimi
                </Label>
                <Input
                  id="application-experience"
                  name="experience"
                  placeholder="Örn. 2 yıl powerlifting"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="application-goal">Ana hedefiniz</Label>
                <Textarea
                  id="application-goal"
                  name="goal"
                  required
                  minLength={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="application-message">
                  Eklemek istedikleriniz
                </Label>
                <Textarea id="application-message" name="message" />
              </div>
              <label className="flex items-start gap-2 text-muted-foreground text-xs">
                <input type="checkbox" required className="mt-0.5" />
                İletişim bilgilerimin başvuruma dönüş amacıyla işlenmesini kabul
                ediyorum.
              </label>
              {mutation.isError && (
                <p className="text-destructive text-sm">
                  Başvuru gönderilemedi. Lütfen tekrar deneyin.
                </p>
              )}
              <Button
                type="submit"
                className="w-full"
                disabled={mutation.isPending}
              >
                {mutation.isPending ? "Gönderiliyor..." : "Başvuruyu Gönder"}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
